import Conversation from '@/components/messaging/Conversation';
import InvitableAffiliateAlert from '@/components/messaging/InvitableAffiliateAlert';
import ParticipantHeader from '@/components/messaging/ParticipantHeader';
import { useConversationDetailsQuery } from '@/hooks/queries/use-conversation-details-query';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import { useAuth } from '@/contexts/auth-context';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { isCreatorParticipant } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsStringLiteral } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const MessageThreadPage = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const brandId = user?.currentBrandId;
  const [filter] = useQueryState(
    'messageFilter',
    parseAsStringLiteral(['inbox', 'sent', 'archived'] as const).withDefault('inbox')
  );
  const {
    data: conversation,
    isLoading,
    error,
  } = useConversationDetailsQuery(brandId, threadId ?? '');

  if (isLoading)
    return (
      <Flex justify="center" align="center" h="full">
        <Spinner size="xl" />
      </Flex>
    );
  if (error || !conversation)
    return (
      <Flex justify="center" align="center" h="full">
        <Text color="red.500">{error?.message ?? t('messages.thread.notFound')}</Text>
      </Flex>
    );

  const invitableParticipant =
    isCreatorParticipant(conversation.participant) &&
    !conversation.hasInvitation &&
    !conversation.isAffiliate
      ? conversation.participant
      : null;

  return (
    <Flex key={threadId} flex="1" direction="column" h="full" position="relative">
      <ParticipantHeader
        conversation={conversation}
        currentFilter={filter}
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={100}
        bg="white/92"
        backdropFilter="blur(12px)"
        h="68px"
      />
      {invitableParticipant && (
        <Box position="absolute" top="68px" left={0} right={0} zIndex={90}>
          <InvitableAffiliateAlert
            participant={invitableParticipant}
            onInvitationSent={() => {
              if (brandId) {
                queryClient.invalidateQueries({
                  queryKey: conversationQueryKeys.detail(brandId, conversation.id),
                });
              }
            }}
          />
        </Box>
      )}
      <Box flex="1" minH={0} pt={invitableParticipant ? '68px' : 0}>
        <Conversation conversation={conversation} />
      </Box>
    </Flex>
  );
};

export default MessageThreadPage;
